<template>
  <v-container id="orders-list-view">
    <material-card
      color="primary"
      icon="mdi-account-multiple"
    >
      <template #title>
        Order Requests
      </template>

      <div>
        <v-data-table
          :headers="headers"
          :items="customerRequests"
          single-expand
          show-expand
          fixed-header
          :height="height"
          item-key="id"
          :loading="loading"
          loading-text="Loading... Please wait"
        >
          <template v-slot:[`item.shipping_address`]="{ item }">
            {{ item.shipping_address.street1 }} {{ item.shipping_address.street2 }} <br>
            {{ item.shipping_address.city }}, {{ item.shipping_address.state }} {{ item.shipping_address.zip }}
          </template>

          <template v-slot:[`item.alerts`]="{ item }">
            <v-chip
              v-if="item.alerts"
              color="warning">
              {{ item.alerts }}
            </v-chip>
          </template>

          <template #item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn @click="expand(!isExpanded)" v-if="item.products.length > 0" icon class="v-data-table__expand-icon" :class="{ 'v-data-table__expand-icon--active': isExpanded }">
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-menu
              bottom
              right
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  dark
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-if="item.customer_id" @click="openCustomer(item.customer_id)">
                  <v-list-item-icon>
                    <v-icon>mdi-account-search</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>View Customer</v-list-item-title>
                </v-list-item>
                <v-list-item @click="setCustomer(item)">
                  <v-list-item-icon>
                    <v-icon>mdi-cart-plus</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Add to Cart</v-list-item-title>
                </v-list-item>
                <v-list-item @click="denyRequest(item.id)">
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Deny Request</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-simple-table
                class="m-2"
                dense
              >
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th class="text-left">ID</th>
                    <th class="text-left">Name</th>
                    <th class="text-left">Mfg</th>
                    <th class="text-left">Part#</th>
                    <th class="text-left">Case Qty</th>
                    <th class="text-left">Price</th>
                    <th class="text-left"></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr
                    v-for="product in item.products"
                    :key="product.id"
                  >
                    <td>{{ product.id }}</td>
                    <td>{{ product.name }}</td>
                    <td>{{ product.mfg_name }}</td>
                    <td>{{ product.mfg_pn }}</td>
                    <td>{{ product.purchase_qty }}</td>
                    <td>{{ product.price }}</td>
                    <td>
                      <v-chip
                        :color="product.status_color">
                        {{ product.status }}
                      </v-chip>
                    </td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </td>
          </template>
          <template v-if="isEnabled('no-data')" v-slot:no-data>
            No recent requests found.
          </template>
        </v-data-table>
      </div>
    </material-card>

    <confirmation-modal ref="confirm" />
    <customer-validation-modal ref="customerValidation"></customer-validation-modal>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'OrderRequests',
  components: {
    MaterialCard: () => import('@/components/MaterialCard'),
    ConfirmationModal: () => import('@/components/ConfirmationModal'),
    CustomerValidationModal: () => import('@/views/components/CustomerValidationModal')
  },
  data () {
    return {
      height: undefined,
      loading: false,
      customerRequests: [],
      selectedProducts: [],
      validationErrors: [],
      selectedCustomerRequest: {},
      customer: {
        id: null,
        fname: '',
        lname: '',
        company_name: '',
        email: '',
        account_number: '',
        site_id: 3,
        contractor_id: null,
        default_shipping: null,
        address: {
          id: null,
          street1: '',
          street2: '',
          city: '',
          state_id: null,
          zip: ''
        }
      }
    }
  },
  computed: {
    ...mapState('auth', {
      user: state => state.user
    }),
    ...mapState('cartManagement', {
      cart: state => state.cart
    }),
    headers () {
      return [
        {
          text: 'Request Date',
          align: 'start',
          value: 'created_at',
          sortable: true
        },
        {
          text: 'ID',
          value: 'id',
          align: ' d-none'
        },
        {
          text: 'Customer ID',
          value: 'customer_id',
          align: ' d-none'
        },
        {
          text: 'Name',
          value: 'full_name'
        },
        {
          text: 'Email',
          value: 'email'
        },
        {
          text: 'Phone',
          value: 'phone'
        },
        {
          text: 'Address',
          value: 'shipping_address'
        },
        {
          text: 'Alerts',
          value: 'alerts'
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false
        }
      ]
    },
    itemHeaders () {
      return [
        {
          text: 'ID',
          align: 'start',
          value: 'id',
          sortable: true
        },
        {
          text: 'Name',
          value: 'name',
          align: ' d-none'
        },
        {
          text: 'Mfg',
          value: 'mfg'
        },
        {
          text: 'Part#',
          value: 'mfg_pn'
        },
        {
          text: 'Qty',
          value: 'purchase_qty'
        },
        {
          text: 'Price',
          value: 'price'
        }
      ]
    }
  },
  methods: {
    ...mapActions('orderManagement', {
      fetchRequests: 'fetchOrderRequests',
      approve: 'approveOrderRequest',
      deny: 'denyOrderRequest',
      updateStatus: 'updateOrderRequest'
    }),
    ...mapActions('cartManagement', {
      createCart: 'createCart',
      addCartCustomer: 'addCustomerToCart',
      addProductToCart: 'addProductToCartOnly'
    }),
    ...mapActions('customerManagement', {
      add: 'addCustomer'
    }),
    init () {
      this.fetchOrderRequests()
    },
    fetchOrderRequests () {
      this.fetchRequests(this.user.model_id)
        .then((response) => {
          this.customerRequests = response
          this.loading = false
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
          this.loading = false
        })
    },
    resetCustomer () {
      this.customer = {
        id: null,
        fname: '',
        lname: '',
        company_name: '',
        email: '',
        account_number: '',
        site_id: 3,
        contractor_id: this.user.model_id,
        default_shipping: null,
        address: {
          id: null,
          street1: '',
          street2: '',
          city: '',
          state_id: null,
          zip: ''
        }
      }
      this.selectedCustomerRequest = {}
    },
    setCustomer (customer) {
      this.resetCustomer()
      this.selectedCustomerRequest = customer
      this.customer.id = customer.customer_id
      this.customer.fname = customer.fname
      this.customer.lname = customer.lname
      this.customer.email = customer.email
      this.customer.default_shipping = customer.default_shipping
      this.customer.address = {
        id: null,
        street1: customer.shipping_address.street1,
        street2: customer.shipping_address.street2,
        city: customer.shipping_address.city,
        state_id: customer.shipping_address.state_id,
        zip: customer.shipping_address.zip
      }

      if (this.customer.id) {
        this.addCustomerToCart(this.customer)
      } else {
        this.addCustomer()
      }
    },
    confirmCartCreate () {
      this.$refs.confirm.open('Create a new cart?', 'Do you want to create a new cart instance?', { color: 'red' })
        .then((confirm) => {
          if (confirm) {
            this.createCart()
              .then(() => { })
              .catch((error) => {
                this.$toast.error(error)
              })
          }
        })
    },
    confirmCartNavigate () {
      this.$refs.confirm.open('Go to cart?', 'Do you want to go to your cart?', { color: 'red' })
        .then((confirm) => {
          if (confirm) {
            this.$router.push({name: 'create'})
          } else {
            this.fetchOrderRequests()
          }
        })
    },
    addCustomer () {
      this.add(this.customer)
        .then((response) => {
          if (response.validation_errors.length > 0) {
            this.validationErrors = true
            this.openValidation(response)
          } else {
            this.addCustomerToCart(response)
          }
        })
        .catch((error) => {
          this.reject(error.data.error)
        })
    },
    addCustomerToCart (customer) {
      if (this.cart.id) {
        this.addCartCustomer({cart_id: this.cart.id, customer_id: customer.id, address_id: customer.default_shipping})
          .then((response) => {
            if (this.selectedCustomerRequest.products.length > 0) {
              this.selectedCustomerRequest.products.forEach((item) => {
                const cartItem = {
                  product: {
                    id: item.id,
                    quantity: 1,
                    frequency: 0,
                    next: this.$moment().add(1, 'days').format('YYYY-MM-DD')
                  },
                  cart_customer_id: response.id
                }

                this.addProductToCart(cartItem)
                  .then(() => {})
                  .catch((error) => {
                    this.$toast.error(error.data ? error.data.error : error)
                  })
              })
            }

            this.approveRequest(this.selectedCustomerRequest.id)
          })
          .catch((error) => {
            this.$toast.error(error)
          })
      } else {
        this.createCart()
          .then((response) => {
            this.addCartCustomer({cart_id: response.id, customer_id: customer.id, address_id: customer.default_shipping})
              .then((response) => {
                this.updateStatus({id: this.selectedCustomerRequest.id, customer_id: customer.id})
                if (this.selectedCustomerRequest.products.length > 0) {
                  this.selectedCustomerRequest.products.each((item) => {
                    const cartItem = {
                      product: {
                        id: item.id,
                        quantity: 1,
                        frequency: 0,
                        next: 1
                      },
                      cart_customer_id: response.id
                    }

                    this.addProductToCart(cartItem)
                      .then(() => {})
                      .catch((error) => {
                        this.$toast.error(error.data ? error.data.error : error)
                      })
                  })

                }

                this.confirmCartNavigate()
              })
              .catch((error) => {
                this.$toast.error(error)
              })
          })
          .catch((error) => {
            this.$toast.error(error)
          })
      }
    },
    approveRequest (id) {
      this.updateStatus({id})
        .then(() => {
          this.confirmCartNavigate()
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
        })
    },
    denyRequest (id) {
      this.deny({id})
        .then((response) => {
          this.fetchOrderRequests()
          this.$toast.success(response.message)
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.error : error)
        })
    },
    openCustomer (id) {
      this.$router.push({name: 'customer_detail', params: {id}})
    },
    openValidation (customer) {
      const options = {
        color: 'primary',
        width: 344,
        height: 200,
        zIndex: 200,
        addToCart: false
      }

      this.$refs.customerValidation.open(customer, options)
        .then((response) => {
          if (response.success) {
            this.validationErrors = false
            this.addCustomerToCart(response.customer)
          } else {
            this.addCustomerToCart(customer)
          }
        })
        .catch((error) => { this.$toast.error(error) })
    },
    isEnabled (slot) {
      const records = this.customerRequests.length

      if (slot === 'no-data' && records === 0) {
        return true
      }

      return slot === 'body' && records > 0
    }
  },
  created () {
    this.init()
  }
}
</script>

<style scoped>

</style>
